import { useEffect, useRef } from 'react';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';
import { supabase } from '@/integrations/supabase/client';
import logger from '@/utils/logger';

/**
 * BackgroundAutomationRunner - Backup Processing Mode
 * 
 * This component only triggers job processing when the cron job
 * hasn't been active recently. It checks for recent job updates
 * to detect if cron is working before triggering.
 * 
 * Processing happens only when:
 * 1. User is logged in
 * 2. There are running jobs
 * 3. Cron hasn't updated jobs in the last 45 seconds
 */
export const BackgroundAutomationRunner = () => {
  const { user } = useSecureAuthContext();
  const intervalRef = useRef<number | null>(null);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    const processRunningJobs = async () => {
      // Prevent overlapping processing
      if (isProcessingRef.current) {
        return;
      }

      try {
        // Check if there are any running jobs
        const { data: runningJobs, error: jobsError } = await supabase
          .from('automated_generation_jobs')
          .select('id, job_name, status, images_generated, total_images_target, updated_at')
          .eq('created_by', user.id)
          .eq('status', 'running')
          .order('updated_at', { ascending: false })
          .limit(5);

        if (jobsError) {
          logger.error('🤖 Auto-Processor: Error checking jobs:', jobsError);
          return;
        }

        if (!runningJobs || runningJobs.length === 0) {
          // No running jobs, skip processing
          return;
        }

        // Check if any jobs still need images
        const jobsNeedingWork = runningJobs.filter(
          j => j.images_generated < j.total_images_target
        );

        if (jobsNeedingWork.length === 0) {
          return;
        }

        // ===== COORDINATION CHECK: See if cron recently processed =====
        // If any job was updated in the last 45 seconds, cron is working - skip
        const now = Date.now();
        const mostRecentUpdate = runningJobs[0]?.updated_at;
        
        if (mostRecentUpdate) {
          const lastUpdateTime = new Date(mostRecentUpdate).getTime();
          const secondsSinceUpdate = (now - lastUpdateTime) / 1000;
          
          if (secondsSinceUpdate < 45) {
            logger.log(`🤖 Auto-Processor: Cron active (${Math.floor(secondsSinceUpdate)}s ago), skipping`);
            return;
          }
        }

        logger.log(`🤖 Auto-Processor: No recent cron activity, triggering backup processing`);
        logger.log(`🤖 Found ${jobsNeedingWork.length} jobs needing processing`);
        isProcessingRef.current = true;

        // Trigger the processing edge function
        const { data, error } = await supabase.functions.invoke('process-automation-job', {
          body: {}
        });

        if (error) {
          logger.error('🤖 Auto-Processor: Error invoking function:', error);
        } else if (data?.results?.length > 0) {
          const totalGenerated = data.results.reduce(
            (sum: number, r: any) => sum + (r.imagesGenerated || r.images_generated || 0), 
            0
          );
          logger.log(`🤖 Auto-Processor: Generated ${totalGenerated} images`);
        } else if (data?.message?.includes('already in progress')) {
          logger.log('🤖 Auto-Processor: Processing already in progress (locked)');
        }
      } catch (error) {
        logger.error('🤖 Auto-Processor: Error:', error);
      } finally {
        isProcessingRef.current = false;
      }
    };

    // Initial check after 10 seconds (give cron time to work first)
    const initialTimeout = setTimeout(processRunningJobs, 10000);

    // Then check every 90 seconds (longer than cron interval to avoid overlap)
    intervalRef.current = window.setInterval(processRunningJobs, 90000);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [user?.id]);

  return null;
};
